<script>
  import firebase from "firebase/compat/app";
  import "firebase/compat/auth";
  import "firebase/compat/firestore";
  import { createEventDispatcher, onMount } from "svelte";
  import { log, announce } from "./Logging.svelte";
  import { newUserDocument } from "../prefsDefaults.js";
  import { ensureDirectoryEntry } from "../directory.js";
  import { ensureProfile } from "../profiles.js";
  import { sendVerificationEmail, sendPasswordResetEmail } from "../verification.js";
  import { passwordRequirements } from "../native.js";
  import { pendingInviter } from "../invites.js";
  import { createdAccountFromUrl } from "../handoff.js";

  const dispatch = createEventDispatcher();
  const MIN_PASSWORD_LENGTH = 8;

  // "signin" | "signup" | "reset"
  let mode = "signin";
  let busy = false;
  let errorMessage = "";
  let infoMessage = "";
  let fieldErrors = {};

  let signin = { email: "", password: "" };
  let signup = { name: "", email: "", password: "" };
  let reset = { email: "" };

  const headings = {
    signin: "Sign In",
    signup: "Create your account",
    reset: "Reset your password",
  };

  // Arriving on a friend's invite link: start on the registration form.
  let inviteSeen = false;
  $: if ($pendingInviter && !inviteSeen) { inviteSeen = true; if (mode === "signin" && !busy) setMode("signup"); }

  function setMode(next) {
    mode = next;
    errorMessage = "";
    infoMessage = "";
    fieldErrors = {};
  }

  const AUTH_ERRORS = {
    "auth/email-already-in-use":
      "An account already exists for that email address. Sign in instead, or reset your password.",
    "auth/invalid-email": "That email address doesn't look right.",
    "auth/weak-password": `Please choose a stronger password: at least ${MIN_PASSWORD_LENGTH} characters, with a capital letter, a number and a symbol.`,
    "auth/user-not-found": "No account was found for that email address.",
    "auth/wrong-password": "That password is incorrect.",
    // With email enumeration protection on, Firebase no longer says which part was wrong.
    "auth/invalid-credential": "That email address or password is incorrect.",
    "auth/invalid-login-credentials": "That email address or password is incorrect.",
    "auth/user-disabled": "This account has been disabled. Contact help@passcall.io.",
    "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
    "auth/network-request-failed": "Network error. Check your connection and try again.",
    "auth/operation-not-allowed": "Email sign-in is not enabled for this project.",
    "permission-denied":
      "Your account was created, but its profile could not be saved. Please contact help@passcall.io.",
  };

  function describeError(error) {
    if (error && error.code === "auth/password-does-not-meet-requirements") return passwordRequirements(error);
    const code = error && error.code;
    if (code && AUTH_ERRORS[code]) return AUTH_ERRORS[code];
    return (error && error.message) || "Something went wrong. Please try again.";
  }

  function validateSignup() {
    const errors = {};
    if (!signup.name.trim()) errors.name = "Please enter your name.";
    if (!signup.email.trim()) errors.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signup.email.trim()))
      errors.email = "That email address doesn't look right.";
    if (signup.password.length < MIN_PASSWORD_LENGTH)
      errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters, with a capital letter, a number and a symbol.`;
    return errors;
  }

  async function submitSignin() {
    busy = true;
    errorMessage = "";
    try {
      await firebase.auth().signInWithEmailAndPassword(signin.email.trim(), signin.password);
      // App.svelte reacts to onAuthStateChanged from here.
    } catch (error) {
      console.log("Auth Error", error);
      errorMessage = describeError(error);
    } finally {
      busy = false;
    }
  }

  async function submitSignup() {
    fieldErrors = validateSignup();
    if (Object.keys(fieldErrors).length > 0) return;
    busy = true;
    errorMessage = "";
    const name = signup.name.trim();
    const email = signup.email.trim();
    try {
      // 1. Auth user
      const credential = await firebase.auth().createUserWithEmailAndPassword(email, signup.password);
      const user = credential.user;
      // 2. Display name on the auth profile
      await user.updateProfile({ displayName: name });
      // The window stays closed until this address is confirmed.
      await sendVerificationEmail(user).catch((e) => console.warn("Verification email not sent", e));
      // 3. Users/{uid} document, in the shape App/Config/FriendList expect
      await firebase
        .firestore()
        .collection("Users")
        .doc(user.uid)
        .set(
          newUserDocument({
            name,
            email: user.email || email,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          })
        );
      // 4. Email -> uid lookup entry so friends can find this account by email
      await ensureDirectoryEntry(firebase.firestore(), user, name).catch((e) => console.warn("Directory entry not written", e));
      // 5. Public profile: the only thing other users can see about this account
      await ensureProfile(firebase.firestore(), user, name).catch((e) => console.warn("Profile not written", e));
      // App.svelte's onAuthStateChanged takes over navigation.
    } catch (error) {
      console.error("Sign-up error", error);
      errorMessage = describeError(error);
    } finally {
      busy = false;
    }
  }

  async function submitReset() {
    busy = true;
    errorMessage = "";
    infoMessage = "";
    try {
      await sendPasswordResetEmail(firebase.auth(), reset.email.trim());
      infoMessage = "If an account exists for that address, a password reset email is on its way.";
    } catch (error) {
      console.log("Reset Error", error);
      if (error && error.code === "auth/user-not-found") {
        // Do not reveal whether an address is registered.
        infoMessage = "If an account exists for that address, a password reset email is on its way.";
      } else {
        errorMessage = describeError(error);
      }
    } finally {
      busy = false;
    }
  }

  onMount(() => {
    announce("Login Mounted");
    // Sent here by the register form on passcall.io when it could not sign the person
    // in directly: the account exists, so ask for the password they just chose.
    const created = createdAccountFromUrl();
    if (created) {
      setMode("signin");
      if (created.email) signin.email = created.email;
      infoMessage = "Your account is ready. Sign in with the password you just chose.";
    }
  });
</script>

<div class="auth">
  <header class="auth__top">
    <div class="auth__bar">
      <a class="auth__brand" href="https://passcall.io/" aria-label="PassCall home">
        <img src="/images/PC-website-hd-logo.png" alt="PassCall" width="299" height="108" />
      </a>
      <nav class="auth__nav" aria-label="Site">
        <a href="https://passcall.io/">Home</a>
        <a href="https://passcall.io/about/">About</a>
        <a href="https://passcall.io/contact/">Contact</a>
      </nav>
      <div class="auth__switch" role="tablist" aria-label="Sign in or register">
        <button type="button" role="tab" aria-selected={mode !== "signup"} class:is-active={mode !== "signup"} on:click={() => setMode("signin")}>Sign in</button>
        <button type="button" role="tab" aria-selected={mode === "signup"} class:is-active={mode === "signup"} on:click={() => setMode("signup")}>Register</button>
      </div>
    </div>
  </header>

  <main class="auth__main">
    <section class="auth__card" aria-labelledby="auth-title">
      {#if $pendingInviter}
        <div class="auth__note auth__note--invite" role="status">
          <strong>{$pendingInviter.name || "A friend"}</strong> invited you to PassCall. {mode === "signup" ? "Create your account" : "Sign in"} and you'll be on each other's friends list straight away.
        </div>
      {/if}
      <p class="auth__eyebrow">{mode === "signup" ? "New to PassCall" : mode === "reset" ? "Your account" : "Welcome back"}</p>
      <h1 id="auth-title" class="auth__title">{headings[mode]}</h1>
      <p class="auth__lede">
        {#if mode === "signup"}
          A minute to set up. Then hang a screen in a room you walk through and add the people you miss bumping into.
        {:else if mode === "reset"}
          Enter your email address and we'll send you a link to choose a new password.
        {:else}
          Sign in to open your window.
        {/if}
      </p>

      {#if errorMessage}
        <div class="auth__note auth__note--error" role="alert">{errorMessage}</div>
      {/if}
      {#if infoMessage}
        <div class="auth__note auth__note--ok" role="status">{infoMessage}</div>
      {/if}

      {#if mode === "signin"}
      <form action="" class="auth__form" on:submit|preventDefault={submitSignin}>
        <div class="field">
          <label for="signin-email" class="label">Email</label>
          <input id="signin-email" type="email" bind:value={signin.email} placeholder="you@example.com" autocomplete="email" class="input" required />
        </div>
        <div class="field">
          <label for="signin-password" class="label">Password</label>
          <input id="signin-password" type="password" placeholder="••••••••" bind:value={signin.password} autocomplete="current-password" class="input" required />
        </div>
        <button class="auth__btn" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        <p class="auth__links">
          <a href="#reset-password" on:click|preventDefault={() => setMode("reset")}>Forgot your password?</a>
          <span>New to PassCall? <a href="#create-account" on:click|preventDefault={() => setMode("signup")}>Create an account</a></span>
        </p>
      </form>

      {:else if mode === "signup"}
      <form action="" class="auth__form" on:submit|preventDefault={submitSignup} novalidate>
        <div class="field">
          <label for="signup-name" class="label">Your name</label>
          <input id="signup-name" type="text" bind:value={signup.name} placeholder="e.g. Bob Smith" autocomplete="name" class="input" class:is-danger={fieldErrors.name} required />
          {#if fieldErrors.name}<p class="help is-danger">{fieldErrors.name}</p>{/if}
        </div>
        <div class="field">
          <label for="signup-email" class="label">Email</label>
          <input id="signup-email" type="email" bind:value={signup.email} placeholder="you@example.com" autocomplete="email" class="input" class:is-danger={fieldErrors.email} required />
          {#if fieldErrors.email}<p class="help is-danger">{fieldErrors.email}</p>{/if}
        </div>
        <div class="field">
          <label for="signup-password" class="label">Password</label>
          <input id="signup-password" type="password" placeholder="{MIN_PASSWORD_LENGTH}+ characters, a capital, a number, a symbol" bind:value={signup.password} autocomplete="new-password" minlength={MIN_PASSWORD_LENGTH} class="input" class:is-danger={fieldErrors.password} required />
          {#if fieldErrors.password}
            <p class="help is-danger">{fieldErrors.password}</p>
          {:else}
            <p class="help">At least {MIN_PASSWORD_LENGTH} characters, with a capital letter, a number and a symbol.</p>
          {/if}
        </div>
        <button class="auth__btn" disabled={busy}>{busy ? "Creating account…" : "Create account"}</button>
        <p class="auth__tip">
          <strong>One setup tip.</strong> Plug your webcam straight into a USB port on the computer itself, not into a hub or a monitor. Over a hub the picture can tear or crawl to a frame a second.
        </p>
        <p class="auth__links">
          <span>Already have an account? <a href="#sign-in" on:click|preventDefault={() => setMode("signin")}>Sign in</a></span>
        </p>
      </form>

      {:else}
      <form action="" class="auth__form" on:submit|preventDefault={submitReset}>
        <div class="field">
          <label for="reset-email" class="label">Email</label>
          <input id="reset-email" type="email" bind:value={reset.email} placeholder="you@example.com" autocomplete="email" class="input" required />
        </div>
        <button class="auth__btn" disabled={busy}>{busy ? "Sending…" : "Send reset email"}</button>
        <p class="auth__links">
          <a href="#sign-in" on:click|preventDefault={() => setMode("signin")}>Back to sign in</a>
        </p>
      </form>
      {/if}
    </section>

    <aside class="auth__aside" aria-hidden="true">
      <div class="auth__frame">
        <picture>
          <source type="image/webp" srcset="/images/hero-window.webp" />
          <img src="/images/hero-window.jpg" width="330" height="534" alt="" />
        </picture>
        <span class="auth__chip"><span class="auth__live"></span>Their place</span>
      </div>
      <p class="auth__tagline">Your display becomes a <em>window</em> to the people you love.</p>
    </aside>
  </main>

  <footer class="auth__foot">
    <span>&copy; 2026 PassCall. All rights reserved.</span>
    <span>Louisville, Kentucky</span>
    <a href="mailto:help@passcall.io">help@passcall.io</a>
  </footer>
</div>

<style>
  .auth { min-height: 100vh; display: flex; flex-direction: column; background: var(--cream); color: var(--text); font-family: var(--font); }

  /* top bar */
  .auth__top { background: var(--wood); color: var(--on-dark); border-bottom: 2px solid var(--wood-trim); box-shadow: 0 6px 18px rgba(0, 0, 0, .35); }
  .auth__bar { max-width: 1200px; margin: 0 auto; padding: 0 16px; min-height: 68px; display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; }
  .auth__brand img { height: 34px; width: auto; filter: brightness(0) invert(1); display: block; }
  .auth__nav { display: flex; gap: 1.5rem; margin-left: auto; }
  .auth__nav a { color: var(--on-dark); text-decoration: none; font-weight: 500; font-size: .95rem; }
  .auth__nav a:hover { color: var(--amber); }
  .auth__tip { margin: 1rem 0 0; padding: .75rem 1rem; border-left: 3px solid var(--amber); background: rgba(0, 0, 0, .04); font-size: .9rem; line-height: 1.45; }
  .auth__switch { display: inline-flex; border: 1.5px solid rgba(244, 237, 227, .35); border-radius: 999px; padding: 3px; }
  .auth__switch button { font: inherit; font-weight: 600; font-size: .9rem; padding: .45rem 1rem; border: 0; border-radius: 999px; background: transparent; color: var(--on-dark); cursor: pointer; }
  .auth__switch button.is-active { background: var(--red-hi); color: #fff; }

  /* main */
  .auth__main { flex: 1; max-width: 1200px; width: 100%; margin: 0 auto; padding: clamp(2rem, 6vw, 5rem) 16px; display: grid; gap: 3rem; align-content: start; }
  @media (min-width: 900px) { .auth__main { grid-template-columns: minmax(0, 520px) 1fr; align-content: center; align-items: center; } }
  .auth__card { background: var(--paper); border: 1px solid var(--line); border-radius: 24px; padding: clamp(1.5rem, 4vw, 2.5rem); box-shadow: 0 30px 60px -40px rgba(27, 21, 19, .35); }
  .auth__eyebrow { margin: 0 0 .9rem; font-size: .78rem; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--red); display: inline-flex; align-items: center; gap: .5rem; }
  .auth__eyebrow::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
  .auth__title { font-family: var(--display); font-weight: 500; font-size: clamp(2rem, 3vw + .5rem, 2.75rem); line-height: 1.05; letter-spacing: -.02em; margin: 0 0 .6rem; color: var(--text); }
  .auth__lede { margin: 0 0 1.5rem; color: var(--muted); line-height: 1.55; }
  .auth__note { border-radius: 12px; padding: .8rem 1rem; margin-bottom: 1.1rem; font-size: .95rem; }
  .auth__note--error { background: #F8ECEC; color: var(--danger); border: 1px solid #EBCACA; }
  .auth__note--ok { background: #E8F5EC; color: #1F6B3A; border: 1px solid #C8E6D1; }
  .auth__note--invite { background: rgba(212, 175, 55, .18); color: var(--text); border: 1px solid rgba(212, 175, 55, .5); margin-bottom: 1rem; }

  .auth__form .label { font-weight: 600; font-size: .95rem; color: var(--text); margin-bottom: .35rem; }
  .auth__form .input { font: inherit; background: var(--cream); border: 1.5px solid var(--line); border-radius: 10px; padding: .8rem .95rem; height: auto; box-shadow: none; }
  .auth__form .input:focus { border-color: var(--red); box-shadow: 0 0 0 3px rgba(31, 107, 63, .18); background: #fff; }
  .auth__form .input.is-danger { border-color: var(--danger); }
  .auth__form .help { color: var(--muted); }
  .auth__form .help.is-danger { color: var(--danger); }
  .auth__btn { font: inherit; font-weight: 600; font-size: 1.05rem; width: 100%; padding: .95rem 1.5rem; border: 0; border-radius: 999px; background: var(--gold-metal); color: var(--ink); cursor: pointer; margin-top: .5rem; transition: background .15s; }
  .auth__btn:hover { filter: brightness(1.06); }
  .auth__btn[disabled] { opacity: .6; cursor: progress; }
  .auth__links { display: flex; flex-direction: column; gap: .4rem; margin: 1.1rem 0 0; font-size: .9rem; color: var(--muted); }
  .auth__links a { color: var(--red); text-decoration: underline; text-underline-offset: .18em; }

  /* aside */
  .auth__aside { display: none; }
  @media (min-width: 900px) { .auth__aside { display: grid; justify-items: center; gap: 1.5rem; } }
  .auth__frame { position: relative; width: 300px; border-radius: 18px; padding: 11px; background: var(--wood); outline: 1px solid var(--wood-trim); outline-offset: -4px; transform: rotate(2deg); box-shadow: 0 40px 70px -25px rgba(0, 0, 0, .6), 0 0 90px -20px rgba(232, 184, 109, .35); }
  .auth__frame img { display: block; width: 100%; aspect-ratio: 330 / 534; object-fit: cover; border-radius: 12px; }
  .auth__chip { position: absolute; left: 16px; bottom: 16px; display: inline-flex; align-items: center; gap: .45rem; padding: .35rem .7rem; border-radius: 999px; background: rgba(21, 16, 14, .72); color: var(--on-dark); font-size: .75rem; font-weight: 600; }
  .auth__live { width: 8px; height: 8px; border-radius: 50%; background: var(--green); }
  .auth__tagline { font-family: var(--display); font-size: 1.75rem; line-height: 1.15; letter-spacing: -.01em; max-width: 16ch; text-align: center; margin: 0; color: var(--text); }
  .auth__tagline em { background: var(--gold-metal); -webkit-background-clip: text; background-clip: text; color: transparent; font-style: italic; }

  /* footer */
  .auth__foot { background: var(--ink); color: var(--on-dark-muted); font-size: .875rem; padding: 1.25rem 16px; display: flex; flex-wrap: wrap; justify-content: center; gap: .5rem 2rem; }
  .auth__foot a { color: var(--on-dark); text-decoration: none; }
  .auth__foot a:hover { color: var(--amber); }
</style>
